import React from 'react';
import omit from 'lodash/omit';
import shuffle from 'lodash/shuffle';
import queryString from 'query-string';

import {
  setLocation,
  setTargetingArguments,
  setDataLayer,
  directSetSeoText,
  seoRequest,
} from 'shared/actions/globals';
import API from 'shared/constants/api';
import { stateDataLayer } from 'shared/modules';
import TITLE from 'shared/constants/seoTitles';
// import HEADER from 'shared/constants/seoHeaders';
// import getInsuranceData from 'shared/routes/insurance';
import { fetchVocabulary } from 'shared/helpers/search';
import { getSeoTextQuery } from 'shared/helpers/seoText';
import DESCRIPTION from 'shared/constants/seoDescriptions';
import { getAdsContext } from 'shared/helpers/adsAndMetrics';
import { clearSelected, selectedActions, setFormVocabulary } from 'shared/actions/searchForm';
import { setHomeCounters } from 'shared/actions/homeSpecialCars';

import topics from 'shared/data/json/topics.json';
import CITIES from 'shared/constants/json/cities.json';
import transportTypes from 'shared/constants/json/transportTypes.json';
import priceStatuses from 'shared/constants/json/priceStatuses.json';
import SECONDARY_LOCATIONS from 'shared/constants/json/secondaryLocations.json';

import Home from './Home';
import Layout from '../../components/Layout';

const CURRENT_TRANSPORT_ID = 1;
const isClient = process.env.BROWSER;

async function action(data) {
  const { fetch, baseUrl, path, store, translate, params } = data;
  await store.dispatch(seoRequest());
  const { globals } = store.getState();
  const locations = [...CITIES, ...SECONDARY_LOCATIONS];
  const { locationSlug } = params;
  const { locale: language } = globals;

  const location = locations.find(item => item.slug === locationSlug) || null;
  if (locationSlug && !location) {
    return null;
  }
  store.dispatch(clearSelected());
  store.dispatch(setLocation(location));
  store.dispatch(selectedActions.set.transport(transportTypes.find(type => type.slug === 'avto')));

  const promisesToAwait = [];

  let brands;
  const locationID = location && location.id;

  if (store.getState().homeCounters && store.getState().homeCounters[locationID || 'all']) {
    brands = store.getState().homeCounters[locationID || 'all'];
  } else {
    promisesToAwait.push(
      fetch(
        `${API.ADVERT.MAKES_COUNT}?${queryString.stringify({
          transport: CURRENT_TRANSPORT_ID,
          location: locationID || undefined,
        })}`,
      )
        .then(r => r.ok && r.json())
        .then(resp => {
          brands = resp;
          store.dispatch(setHomeCounters({ [locationID || 'all']: resp }));
        }),
    );
  }
  let news = undefined;
  const homeNewsRubrics = ['news', 'promo', 'stati', 'obzory', 'test-drive'];
  promisesToAwait.push(
    fetch(
      API.JOURNAL.LIST({
        tag: undefined,
        page: 1,
        perPage: 4,
        mainPage: 1,
        rubric: homeNewsRubrics.join(','),
        language,
      }),
    )
      .then(r => r.json())
      .then(r => r.results)
      .then(resp => {
        news = resp ? resp.map(item => omit(item, ['content'])) : [];
      }),
  );

  let newCars = {};
  promisesToAwait.push(
    fetch(API.NEW_CARS).then(r => r.ok && r.json()).then(resp => {
      newCars = resp || {};
    }),
  );

  let videos = undefined;
  promisesToAwait.push(
    fetch(
      API.JOURNAL.LIST({
        tag: undefined,
        page: 1,
        perPage: 4,
        mainPage: 1,
        rubric: 'video',
        language,
      }),
    )
      .then(r => r.json())
      .then(r => r.results)
      .then(resp => {
        videos = resp ? resp.map(item => omit(item, ['content'])) : [];
      }),
  );

  let specialCars;
  const PRICE_FROM = 1000000;
  const CARS_IN_LIST = 2;
  // const LITVA_ID = 4482;
  const vipParam = { is_vip: CURRENT_TRANSPORT_ID };
  const priceParam = { price_from: PRICE_FROM };

  const seoTextQuery = getSeoTextQuery(baseUrl, `${path}/index`, language === 'uk', 1);

  promisesToAwait.push(
    Promise.all([
      fetch(
        API.ADVERT.ALL(
          `?${queryString.stringify({ ...vipParam, transport: CURRENT_TRANSPORT_ID })}`,
        ),
      ).then(r => r && r.json && r.json()),
      //
      fetch(
        API.ADVERT.ALL(
          `?${queryString.stringify({ ...priceParam, transport: CURRENT_TRANSPORT_ID })}`,
        ),
      )
        .then(r => r && r.json && r.json())
        .then(r => r.results),
      //
      [],
      // fetch(
      //   API.ADVERT.ALL(
      //     `?${queryString.stringify({ location: LITVA_ID, transport: CURRENT_TRANSPORT_ID })}`,
      //   ),
      // )
      //   .then(r => r && r.json && r.json())
      //   .then(r => r.results),
      //
      fetch(API.SEO.SEARCH_TEXT(seoTextQuery)).then(r => r && r.json && r.json()),
    ]).then(allResults => {
      const [vipCars, lowPriceCars, litvaCars, seoText] = allResults;
      specialCars = {
        vipCars,
        lowPriceCars,
        europeCars: shuffle(litvaCars || []).slice(0, CARS_IN_LIST),
      };
      // store.dispatch(setHomeSpecialCars(specialCars));
      store.dispatch(directSetSeoText(seoText && seoText.err ? seoText.err : seoText));
    }),
  );

  const titleLocation = location && location.where ? location.where[globals.locale] : undefined;

  await Promise.all(promisesToAwait);

  const metaInfo = {
    ...(titleLocation ? { location: { ...location, where: titleLocation } } : { location }),
  };

  const title = TITLE.HOME(metaInfo, translate);
  const description = DESCRIPTION.HOME(metaInfo, translate);
  // const seoHeader = HEADER.HOME(metaInfo, translate);
  const targetingArguments = getAdsContext({
    isMain: true,
    location,
  });
  store.dispatch(setTargetingArguments(targetingArguments));
  store.dispatch(
    setDataLayer(
      stateDataLayer.manipulation({ data: specialCars.vipCars, pagetype: 'searchresults' }),
    ),
  );
  const isLocalUk = language === 'uk';

  if (isClient) {
    // getInsuranceData(data);

    const state = store.getState();
    const { searchForm: { vocabulary } } = state;

    // TODO: change to getting TransportType selected from menu. (design wasn't ready )
    const neededVocabulary = await fetchVocabulary(vocabulary, { slug: 'avto' }, fetch);
    store.dispatch(
      setFormVocabulary({
        ...neededVocabulary,
        priceStatuses,
      }),
    );
  }

  return {
    chunks: ['home'],
    title,
    description,
    routeName: 'home',
    component: (
      <Layout withBranding>
        <Home
          news={news}
          newCars={newCars}
          videos={videos}
          specialCars={specialCars}
          topics={topics}
          brands={brands}
          routeLocation={location}
          isLocalUk={isLocalUk}
        />
      </Layout>
    ),
  };
}

export default action;



// WEBPACK FOOTER //
// ./src/desktop/routes/home/index.js